import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, FileText, AlertCircle, Download, Printer, History, X, Trash2, ChevronRight, Microscope, ArrowLeft, CreditCard } from 'lucide-react';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import LandingPage from './components/LandingPage';
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get or create user ID from localStorage
const getUserId = () => {
  let userId = localStorage.getItem('hikari_user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('hikari_user_id', userId);
  }
  return userId;
};

// Comprehensive test definitions with descriptions and reference ranges
const TESTS = {
  human: {
    "Complete Blood Count (CBC)": {
      icon: "🩸",
      markers: [
        { name: "Hemoglobin", unit: "g/dL", low: 12, high: 17.5, description: "Oxygen-carrying protein in red blood cells" },
        { name: "Hematocrit", unit: "%", low: 36, high: 52, description: "Percentage of blood volume made up of red blood cells" },
        { name: "WBC (White Blood Cells)", unit: "K/µL", low: 4, high: 11, description: "Immune system cells that fight infection" },
        { name: "Platelets", unit: "K/µL", low: 150, high: 400, description: "Cells that help blood clot" },
        { name: "RBC (Red Blood Cells)", unit: "M/µL", low: 4.2, high: 5.9, description: "Cells that carry oxygen throughout the body" },
        { name: "MCV", unit: "fL", low: 80, high: 100, description: "Mean corpuscular volume; size of red blood cells" },
        { name: "MCH", unit: "pg", low: 27, high: 33, description: "Average hemoglobin per red blood cell" },
        { name: "MCHC", unit: "g/dL", low: 32, high: 36, description: "Hemoglobin concentration in red cells" },
        { name: "Neutrophils", unit: "%", low: 50, high: 70, description: "Most common white blood cell; fights bacteria" },
        { name: "Lymphocytes", unit: "%", low: 20, high: 40, description: "Immune cells that fight viruses" },
        { name: "Monocytes", unit: "%", low: 2, high: 8, description: "White cells that clean up dead tissue" },
        { name: "Eosinophils", unit: "%", low: 1, high: 4, description: "Cells involved in allergies and parasites" },
        { name: "Basophils", unit: "%", low: 0, high: 1, description: "Rare white cells involved in inflammation" },
      ]
    },
    "Metabolic Panel (BMP/CMP)": {
      icon: "⚗️",
      markers: [
        { name: "Glucose", unit: "mg/dL", low: 70, high: 99, description: "Blood sugar level (fasting)" },
        { name: "Creatinine", unit: "mg/dL", low: 0.6, high: 1.2, description: "Kidney filtration waste product" },
        { name: "BUN (Blood Urea Nitrogen)", unit: "mg/dL", low: 7, high: 20, description: "Kidney function marker" },
        { name: "eGFR", unit: "mL/min", low: 60, high: 999, description: "Estimated kidney filtration rate; higher is better" },
        { name: "Sodium", unit: "mEq/L", low: 136, high: 145, description: "Electrolyte controlling fluid balance" },
        { name: "Potassium", unit: "mEq/L", low: 3.5, high: 5.1, description: "Electrolyte critical for heart & muscle function" },
        { name: "Chloride", unit: "mEq/L", low: 98, high: 107, description: "Electrolyte for fluid/acid-base balance" },
        { name: "CO2 (Bicarbonate)", unit: "mEq/L", low: 22, high: 29, description: "Acid-base balance marker" },
        { name: "Calcium", unit: "mg/dL", low: 8.5, high: 10.5, description: "Bone, nerve, and muscle function" },
        { name: "Total Protein", unit: "g/dL", low: 6.3, high: 8.2, description: "Overall protein; reflects nutrition and liver" },
        { name: "Albumin", unit: "g/dL", low: 3.5, high: 5.0, description: "Main blood protein; carries nutrients" },
        { name: "ALT", unit: "U/L", low: 7, high: 56, description: "Liver enzyme; elevated indicates liver stress" },
        { name: "AST", unit: "U/L", low: 10, high: 40, description: "Liver/heart enzyme" },
        { name: "ALP", unit: "U/L", low: 44, high: 147, description: "Liver/bone enzyme" },
        { name: "Bilirubin (Total)", unit: "mg/dL", low: 0.1, high: 1.2, description: "Liver waste product; high = jaundice risk" },
      ]
    },
    "Lipid Panel": {
      icon: "💛",
      markers: [
        { name: "Total Cholesterol", unit: "mg/dL", low: 0, high: 200, description: "Total fat-like substance in blood" },
        { name: "LDL Cholesterol", unit: "mg/dL", low: 0, high: 100, description: "\"Bad\" cholesterol; high levels increase heart risk" },
        { name: "HDL Cholesterol", unit: "mg/dL", low: 40, high: 999, description: "\"Good\" cholesterol; higher is better" },
        { name: "Triglycerides", unit: "mg/dL", low: 0, high: 150, description: "Type of fat in blood related to diet" },
        { name: "Non-HDL Cholesterol", unit: "mg/dL", low: 0, high: 130, description: "All bad cholesterol combined" },
        { name: "VLDL Cholesterol", unit: "mg/dL", low: 2, high: 30, description: "Very low density lipoprotein" },
      ]
    },
    "Thyroid Panel": {
      icon: "🦋",
      markers: [
        { name: "TSH", unit: "mIU/L", low: 0.4, high: 4.0, description: "Thyroid stimulating hormone; master regulator" },
        { name: "Free T4", unit: "ng/dL", low: 0.8, high: 1.8, description: "Active thyroid hormone" },
        { name: "Free T3", unit: "pg/mL", low: 2.3, high: 4.2, description: "Most active form of thyroid hormone" },
        { name: "Total T4", unit: "µg/dL", low: 4.5, high: 12.5, description: "Total thyroxine in blood" },
        { name: "Total T3", unit: "ng/dL", low: 80, high: 200, description: "Total triiodothyronine" },
        { name: "Anti-TPO Antibodies", unit: "IU/mL", low: 0, high: 34, description: "Thyroid autoimmunity marker" },
      ]
    },
    "HbA1c / Diabetes": {
      icon: "📊",
      markers: [
        { name: "HbA1c", unit: "%", low: 0, high: 5.7, description: "3-month average blood sugar control" },
        { name: "Fasting Glucose", unit: "mg/dL", low: 70, high: 99, description: "Blood sugar after 8+ hours fasting" },
        { name: "Insulin (Fasting)", unit: "µIU/mL", low: 2, high: 25, description: "Hormone regulating glucose uptake" },
        { name: "C-Peptide", unit: "ng/mL", low: 0.5, high: 2.0, description: "Measures insulin production" },
      ]
    },
    "Iron & Vitamins": {
      icon: "🔩",
      markers: [
        { name: "Serum Iron", unit: "µg/dL", low: 60, high: 170, description: "Amount of iron in blood" },
        { name: "TIBC", unit: "µg/dL", low: 240, high: 450, description: "Total iron binding capacity" },
        { name: "Transferrin Saturation", unit: "%", low: 20, high: 50, description: "How much iron transport protein is used" },
        { name: "Ferritin", unit: "ng/mL", low: 12, high: 300, description: "Iron storage protein; low = iron deficiency" },
        { name: "Vitamin D (25-OH)", unit: "ng/mL", low: 30, high: 100, description: "Bone, immune, and mood support vitamin" },
        { name: "Vitamin B12", unit: "pg/mL", low: 200, high: 900, description: "Nerve function and energy" },
        { name: "Folate", unit: "ng/mL", low: 2.7, high: 17, description: "Cell growth and DNA repair" },
        { name: "Magnesium", unit: "mg/dL", low: 1.7, high: 2.2, description: "Muscle, nerve, and sleep mineral" },
        { name: "Zinc", unit: "µg/dL", low: 60, high: 120, description: "Immune function and wound healing" },
      ]
    },
    "Cardiac & Inflammation": {
      icon: "❤️",
      markers: [
        { name: "CRP (C-Reactive Protein)", unit: "mg/L", low: 0, high: 3.0, description: "Inflammation marker; elevated in infection/heart risk" },
        { name: "ESR", unit: "mm/hr", low: 0, high: 20, description: "Erythrocyte sedimentation rate; inflammation marker" },
        { name: "Fibrinogen", unit: "mg/dL", low: 200, high: 400, description: "Clotting factor; elevated in inflammation" },
        { name: "Homocysteine", unit: "µmol/L", low: 0, high: 15, description: "Amino acid; high levels = heart disease risk" },
        { name: "Troponin I", unit: "ng/mL", low: 0, high: 0.04, description: "Heart muscle damage marker" },
        { name: "BNP", unit: "pg/mL", low: 0, high: 100, description: "Heart failure marker" },
        { name: "LDH", unit: "U/L", low: 122, high: 222, description: "Cell damage enzyme" },
      ]
    },
    "Hormones": {
      icon: "🌡️",
      markers: [
        { name: "Cortisol (Morning)", unit: "µg/dL", low: 6, high: 23, description: "Stress hormone; peaks in the morning" },
        { name: "DHEA-S", unit: "µg/dL", low: 80, high: 560, description: "Adrenal hormone; declines with age" },
        { name: "Testosterone (Total)", unit: "ng/dL", low: 300, high: 1000, description: "Main male sex hormone; present in both sexes" },
        { name: "Estradiol (E2)", unit: "pg/mL", low: 15, high: 350, description: "Primary estrogen; varies by sex/cycle phase" },
        { name: "Progesterone", unit: "ng/mL", low: 0.1, high: 25, description: "Varies by menstrual cycle phase" },
        { name: "LH", unit: "IU/L", low: 1.5, high: 12, description: "Luteinizing hormone; regulates sex hormones" },
        { name: "FSH", unit: "IU/L", low: 1.5, high: 12, description: "Follicle stimulating hormone" },
        { name: "Prolactin", unit: "ng/mL", low: 2, high: 29, description: "Hormone involved in milk production" },
        { name: "IGF-1", unit: "ng/mL", low: 88, high: 246, description: "Growth hormone activity marker" },
      ]
    },
    "Urinalysis": {
      icon: "🧪",
      markers: [
        { name: "pH", unit: "", low: 4.5, high: 8.0, description: "Urine acidity; normal range is slightly acidic" },
        { name: "Specific Gravity", unit: "", low: 1.005, high: 1.030, description: "Urine concentration" },
        { name: "Protein (Urine)", unit: "mg/dL", low: 0, high: 14, description: "Protein in urine; elevated = kidney concern" },
        { name: "Glucose (Urine)", unit: "mg/dL", low: 0, high: 0, description: "Sugar in urine; absent normally" },
        { name: "WBC (Urine)", unit: "/hpf", low: 0, high: 5, description: "White cells in urine; high = infection" },
        { name: "RBC (Urine)", unit: "/hpf", low: 0, high: 2, description: "Red cells in urine; high = bleeding or kidney issue" },
      ]
    },
  },
  animal: {
    "Dog CBC": {
      icon: "🐕",
      markers: [
        { name: "Hemoglobin", unit: "g/dL", low: 12, high: 18, description: "Oxygen-carrying protein; low may indicate anemia" },
        { name: "WBC", unit: "K/µL", low: 6, high: 17, description: "White blood cells; elevated may indicate infection" },
        { name: "Platelets", unit: "K/µL", low: 200, high: 500, description: "Clotting cells; low may cause bruising" },
        { name: "Hematocrit", unit: "%", low: 37, high: 55, description: "Red cell proportion of blood volume" },
        { name: "Neutrophils", unit: "%", low: 60, high: 77, description: "Primary infection-fighting white cell" },
        { name: "Lymphocytes", unit: "%", low: 12, high: 30, description: "Viral defense cells" },
        { name: "Monocytes", unit: "%", low: 3, high: 10, description: "Cleanup white cells" },
      ]
    },
    "Dog Metabolic Panel": {
      icon: "🐩",
      markers: [
        { name: "ALT", unit: "U/L", low: 10, high: 125, description: "Liver enzyme; elevated = liver concerns" },
        { name: "AST", unit: "U/L", low: 23, high: 66, description: "Liver and muscle enzyme" },
        { name: "ALP", unit: "U/L", low: 23, high: 212, description: "Liver and bone enzyme" },
        { name: "BUN", unit: "mg/dL", low: 7, high: 27, description: "Kidney function; elevated with poor kidney function" },
        { name: "Creatinine", unit: "mg/dL", low: 0.5, high: 1.8, description: "Kidney filtration marker" },
        { name: "Glucose", unit: "mg/dL", low: 74, high: 143, description: "Blood sugar; high may indicate diabetes" },
        { name: "Calcium", unit: "mg/dL", low: 8.7, high: 11.8, description: "Bone and muscle function electrolyte" },
        { name: "Phosphorus", unit: "mg/dL", low: 2.5, high: 6.8, description: "Bone health and kidney function" },
        { name: "Total Protein", unit: "g/dL", low: 5.2, high: 8.2, description: "Nutritional and liver status" },
        { name: "Albumin", unit: "g/dL", low: 2.2, high: 3.9, description: "Main blood protein" },
      ]
    },
    "Cat CBC": {
      icon: "🐈",
      markers: [
        { name: "Hemoglobin", unit: "g/dL", low: 8, high: 15, description: "Oxygen-carrying protein in red cells" },
        { name: "WBC", unit: "K/µL", low: 5.5, high: 19.5, description: "Immune cells; high may indicate infection" },
        { name: "Hematocrit", unit: "%", low: 24, high: 45, description: "Red cell proportion; low = anemia" },
        { name: "Platelets", unit: "K/µL", low: 200, high: 500, description: "Clotting cells" },
        { name: "Neutrophils", unit: "%", low: 35, high: 75, description: "Infection-fighting white cells" },
        { name: "Lymphocytes", unit: "%", low: 20, high: 55, description: "Viral defense cells" },
      ]
    },
    "Cat Metabolic Panel": {
      icon: "🐱",
      markers: [
        { name: "BUN", unit: "mg/dL", low: 14, high: 36, description: "Kidney waste marker; elevated in kidney disease" },
        { name: "Creatinine", unit: "mg/dL", low: 0.6, high: 2.4, description: "Kidney function; critical to monitor in older cats" },
        { name: "SDMA", unit: "µg/dL", low: 0, high: 14, description: "Early kidney disease marker; more sensitive than creatinine" },
        { name: "ALT", unit: "U/L", low: 12, high: 130, description: "Liver enzyme" },
        { name: "ALP", unit: "U/L", low: 14, high: 111, description: "Liver/bone enzyme" },
        { name: "Glucose", unit: "mg/dL", low: 64, high: 170, description: "Blood sugar; stress can elevate in cats" },
        { name: "Total Protein", unit: "g/dL", low: 5.2, high: 8.8, description: "Overall protein status" },
        { name: "Phosphorus", unit: "mg/dL", low: 3.1, high: 7.5, description: "Kidney and bone health" },
        { name: "Potassium", unit: "mEq/L", low: 3.5, high: 5.8, description: "Heart and muscle electrolyte" },
      ]
    },
    "Horse Panel": {
      icon: "🐴",
      markers: [
        { name: "WBC", unit: "K/µL", low: 5.4, high: 14.3, description: "Immune system cells" },
        { name: "Hemoglobin", unit: "g/dL", low: 11, high: 19, description: "Oxygen transport protein" },
        { name: "Fibrinogen", unit: "mg/dL", low: 100, high: 400, description: "Inflammation marker" },
        { name: "Creatinine", unit: "mg/dL", low: 0.5, high: 1.8, description: "Kidney function marker" },
        { name: "AST", unit: "U/L", low: 226, high: 366, description: "Muscle & liver enzyme" },
        { name: "GGT", unit: "U/L", low: 6, high: 32, description: "Liver enzyme" },
        { name: "Glucose", unit: "mg/dL", low: 75, high: 115, description: "Blood sugar" },
      ]
    }
  }
};

// Status Badge Component
const StatusBadge = ({ value, low, high }) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  
  if (num < low) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30">
        ⬇ LOW
      </span>
    );
  }
  if (num > high) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30">
        ⬆ HIGH
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
      ✓ NORMAL
    </span>
  );
};

// Range Bar Component
const RangeBar = ({ value, low, high }) => {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  
  const min = low - (high - low) * 0.3;
  const max = high + (high - low) * 0.3;
  const pct = Math.min(100, Math.max(0, ((num - min) / (max - min)) * 100));
  const lowPct = ((low - min) / (max - min)) * 100;
  const highPct = ((high - min) / (max - min)) * 100;
  const color = num < low ? "#3B82F6" : num > high ? "#EF4444" : "#10B981";
  
  return (
    <div className="relative h-1.5 bg-white/10 rounded mt-1.5">
      <div 
        className="absolute h-full bg-emerald-500/25 rounded"
        style={{ left: `${lowPct}%`, right: `${100 - highPct}%` }}
      />
      <div 
        className="absolute w-3 h-3 rounded-full -top-[3px] shadow-lg"
        style={{ 
          left: `${pct}%`, 
          transform: 'translateX(-50%)',
          backgroundColor: color,
          boxShadow: `0 0 0 2px #0D2340, 0 0 0 3px ${color}`
        }}
      />
    </div>
  );
};

// Upload Zone Component
const UploadZone = ({ onFileSelect, uploadedFile }) => {
  const fileRef = useRef();
  const [dragging, setDragging] = useState(false);
  
  const isPDF = (name = "") => name.toLowerCase().endsWith(".pdf");
  const fileLabel = (name) => isPDF(name) ? "PDF" : "Image";
  
  const handleFile = (file) => {
    if (!file) return;
    const ok = file.type.startsWith("image/") || file.type === "application/pdf" || file.name.endsWith(".pdf");
    if (!ok) return;
    const reader = new FileReader();
    reader.onload = (e) => onFileSelect(e.target.result, file.name, file.type);
    reader.readAsDataURL(file);
  };
  
  return (
    <div
      onClick={() => fileRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      className={`rounded-xl text-center cursor-pointer transition-all border-2 border-dashed ${
        dragging 
          ? 'border-[#5eead4] bg-[#5eead4]/5' 
          : uploadedFile 
            ? 'border-[#5eead4]/50 bg-[#5eead4]/5' 
            : 'border-white/20 bg-white/5 hover:border-[#5eead4]/50'
      } ${uploadedFile ? 'p-4' : 'p-8'}`}
      data-testid="upload-zone"
    >
      <input 
        ref={fileRef} 
        type="file" 
        accept="image/*,.pdf,application/pdf" 
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
        data-testid="file-input"
      />
      
      {uploadedFile ? (
        <div className="flex items-center gap-3">
          {isPDF(uploadedFile.name) ? (
            <div className="w-14 h-14 rounded-lg border border-[#5eead4]/30 bg-[#5eead4]/10 flex items-center justify-center text-3xl flex-shrink-0">
              📋
            </div>
          ) : (
            <img 
              src={uploadedFile.dataUrl} 
              alt="preview" 
              className="w-14 h-14 object-cover rounded-lg border border-[#5eead4]/30 flex-shrink-0"
            />
          )}
          <div className="text-left">
            <p className="text-[#5eead4] text-sm font-medium">
              ✓ {uploadedFile.name}
            </p>
            <p className="text-white/30 text-xs mt-0.5">
              Tap to replace · Drag & drop another
            </p>
            <span className="inline-block mt-1 text-xs text-[#5eead4] bg-[#5eead4]/10 border border-[#5eead4]/25 px-2 py-0.5 rounded-full">
              {fileLabel(uploadedFile.name)}
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="text-4xl mb-3 flex justify-center gap-2">
            <span>📋</span>
            <span>🖼️</span>
          </div>
          <p className="text-white/80 text-sm font-medium mb-1">
            Upload your lab report
          </p>
          <p className="text-white/35 text-xs mb-3">
            PDF document · Photo or screenshot · Drag & drop or tap
          </p>
          <div className="flex gap-1.5 justify-center">
            {["PDF", "JPG", "PNG", "HEIC"].map(f => (
              <span key={f} className="text-[10px] text-white/40 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                {f}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Results Table Component
const ResultsTable = ({ results, printRef }) => {
  if (!results || results.length === 0) return null;
  
  const getFlagColor = (flag) => {
    if (flag === "High") return { color: "#EF4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.25)" };
    if (flag === "Low") return { color: "#3B82F6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.25)" };
    return { color: "#10B981", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.2)" };
  };
  
  const highCount = results.filter(r => r.flag === "High").length;
  const lowCount = results.filter(r => r.flag === "Low").length;
  const normalCount = results.filter(r => r.flag === "Normal").length;
  
  return (
    <div ref={printRef} className="print-friendly">
      {/* Summary Badges */}
      <div className="flex items-center gap-4 mb-4 text-xs" data-testid="results-summary">
        <span className="text-white/60">
          ✓ {results.length} Tests Found
        </span>
        <div className="flex gap-3">
          {highCount > 0 && <span className="text-red-400">⬆ {highCount} High</span>}
          {lowCount > 0 && <span className="text-blue-400">⬇ {lowCount} Low</span>}
          {normalCount > 0 && <span className="text-emerald-400">✓ {normalCount} OK</span>}
        </div>
      </div>
      
      {/* Results List */}
      <div className="space-y-0.5" data-testid="results-table">
        {results.map((result, idx) => {
          const c = getFlagColor(result.flag);
          return (
            <div 
              key={idx} 
              className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors"
              data-testid={`result-row-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-white/85 text-sm font-medium">{result.testName}</span>
                {result.refLow && result.refHigh && (
                  <span className="text-white/30 text-xs ml-2">
                    ref {result.refLow}–{result.refHigh}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-white text-sm font-semibold">
                  {result.value}
                  <span className="text-white/40 font-normal text-xs ml-1">{result.unit}</span>
                </span>
                <span 
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[52px] text-center"
                  style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}
                >
                  {result.flag === "High" ? "⬆ HIGH" : result.flag === "Low" ? "⬇ LOW" : "✓ OK"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Manual Entry Component
const ManualEntryTab = ({ mode, onResults, setLoading, loading }) => {
  const panels = TESTS[mode];
  const panelNames = Object.keys(panels);
  const [selectedPanel, setSelectedPanel] = useState(panelNames[0]);
  const [values, setValues] = useState({});
  
  const currentPanel = panels[selectedPanel];
  const hasValues = Object.values(values).some(v => v !== "" && v !== undefined);
  
  useEffect(() => {
    if (panelNames.length > 0 && !panelNames.includes(selectedPanel)) {
      setSelectedPanel(panelNames[0]);
    }
  }, [mode, panelNames, selectedPanel]);
  
  const handleSubmit = () => {
    if (!currentPanel) return;
    
    const results = currentPanel.markers
      .filter(m => values[m.name] !== "" && values[m.name] !== undefined)
      .map(m => {
        const val = parseFloat(values[m.name]);
        let flag = 'Normal';
        if (!isNaN(val)) {
          if (val < m.low) flag = 'Low';
          else if (val > m.high) flag = 'High';
        }
        
        return {
          testName: m.name,
          value: values[m.name],
          unit: m.unit,
          refLow: String(m.low),
          refHigh: String(m.high),
          flag
        };
      });
    
    if (results.length > 0) {
      onResults(results, selectedPanel);
    }
  };
  
  if (!currentPanel) return null;
  
  return (
    <div className="space-y-6">
      {/* Panel Selector */}
      <div>
        <p className="text-white/40 text-[11px] uppercase tracking-widest mb-2">Select Panel</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(panels).map(([name, data]) => (
            <button
              key={name}
              onClick={() => { setSelectedPanel(name); setValues({}); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                selectedPanel === name
                  ? 'bg-[#5eead4]/15 text-[#5eead4] border-[#5eead4]/40'
                  : 'bg-white/5 text-white/50 border-white/10 hover:border-white/20'
              }`}
              data-testid={`panel-${name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {data.icon} {name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Entry Form */}
      <div className="glass-card rounded-xl p-5">
        <h2 className="text-white text-lg font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          {currentPanel.icon} {selectedPanel}
        </h2>
        <p className="text-white/35 text-xs mb-5">
          Enter values from your report. Leave blank if not tested.
        </p>
        
        <div className="space-y-5">
          {currentPanel.markers.map(m => {
            const val = values[m.name] || "";
            return (
              <div key={m.name}>
                <div className="flex items-center justify-between mb-0.5">
                  <div>
                    <span className="text-white/85 text-sm font-medium">{m.name}</span>
                    <span className="text-white/30 text-xs ml-2">{m.unit}</span>
                  </div>
                  <StatusBadge value={val} low={m.low} high={m.high} />
                </div>
                <p className="text-white/30 text-xs mb-1.5">{m.description}</p>
                <input
                  type="number"
                  step="any"
                  placeholder={`Ref: ${m.low}–${m.high} ${m.unit}`}
                  className="w-full bg-white/10 border border-white/15 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#5eead4] focus:bg-[#5eead4]/10 placeholder:text-white/25 transition-all"
                  value={val}
                  onChange={e => setValues(prev => ({ ...prev, [m.name]: e.target.value }))}
                  data-testid={`input-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
                />
                {val && <RangeBar value={val} low={m.low} high={m.high} />}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!hasValues || loading}
        className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all ${
          hasValues && !loading
            ? 'bg-gradient-to-r from-[#5eead4] to-[#06b6d4] text-[#0D2340] hover:shadow-lg hover:shadow-[#5eead4]/25 hover:-translate-y-0.5'
            : 'bg-white/10 text-white/25 cursor-not-allowed'
        }`}
        data-testid="submit-manual-entry"
      >
        {loading ? "⏳ Interpreting..." : "✨ Interpret My Results"}
      </button>
    </div>
  );
};

// Upload Tab Component
const UploadTab = ({ mode, onResults, setLoading, loading }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedResults, setExtractedResults] = useState(null);
  const [error, setError] = useState(null);
  
  const handleFileSelect = (dataUrl, name, mimeType) => {
    const isPDF = name.toLowerCase().endsWith(".pdf");
    setUploadedFile({ 
      dataUrl, 
      name, 
      mimeType: mimeType || (isPDF ? "application/pdf" : "image/jpeg") 
    });
    setExtractedResults(null);
    setError(null);
  };
  
  const extractFromFile = async () => {
    if (!uploadedFile) return;
    setExtracting(true);
    setExtractedResults(null);
    setError(null);
    
    try {
      const base64 = uploadedFile.dataUrl.split(",")[1];
      const ext = uploadedFile.name.split('.').pop().toLowerCase();
      const fileType = ext === 'jpg' ? 'jpeg' : ext;
      
      const response = await axios.post(`${API}/lab/extract`, {
        file_data: base64,
        file_type: fileType,
        mode: mode
      });
      
      setExtractedResults(response.data.results);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to extract lab results. Make sure the file is clear and try again.');
    } finally {
      setExtracting(false);
    }
  };
  
  const handleInterpret = () => {
    if (extractedResults && extractedResults.length > 0) {
      onResults(extractedResults);
    }
  };
  
  const isPDF = (name = "") => name.toLowerCase().endsWith(".pdf");
  const fileLabel = (name) => isPDF(name) ? "PDF" : "Image";
  
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-xl p-5">
        <h2 className="text-white text-lg font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          Upload Your Lab Report
        </h2>
        <p className="text-white/35 text-xs mb-4">
          Upload a <strong className="text-white/60">PDF</strong> from your patient portal, or a{' '}
          <strong className="text-white/60">photo / screenshot</strong> of your results — Claude reads all of it.
        </p>
        
        <UploadZone onFileSelect={handleFileSelect} uploadedFile={uploadedFile} />
        
        {uploadedFile && !extractedResults && (
          <button
            onClick={extractFromFile}
            disabled={extracting}
            className={`w-full mt-4 py-3 rounded-xl font-semibold text-sm transition-all ${
              extracting
                ? 'bg-white/10 text-white/30'
                : 'bg-gradient-to-r from-[#5eead4] to-[#06b6d4] text-[#0D2340] hover:shadow-lg hover:shadow-[#5eead4]/25'
            }`}
            data-testid="extract-button"
          >
            {extracting 
              ? `⏳ Reading ${isPDF(uploadedFile.name) ? "PDF" : "image"}...`
              : `🔍 Read & Extract All Values`}
          </button>
        )}
        
        {uploadedFile && extractedResults && (
          <button
            onClick={() => { setUploadedFile(null); setExtractedResults(null); }}
            className="w-full mt-3 py-2 rounded-lg bg-white/5 text-white/40 text-xs border border-white/10 hover:bg-white/10 transition-all"
          >
            ↩ Upload a different report
          </button>
        )}
      </div>
      
      {error && (
        <div className="glass-card rounded-xl p-4 border border-red-500/30">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}
      
      {extractedResults && extractedResults.length > 0 && (
        <div className="glass-card rounded-xl p-5 border border-[#5eead4]/20">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-white text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              ✓ {extractedResults.length} Tests Found
            </h3>
            <span className="text-[10px] text-[#5eead4] bg-[#5eead4]/10 border border-[#5eead4]/20 px-2 py-0.5 rounded-full">
              {fileLabel(uploadedFile?.name || "")}
            </span>
          </div>
          
          <ResultsTable results={extractedResults} />
          
          <button
            onClick={handleInterpret}
            disabled={loading}
            className={`w-full mt-4 py-3 rounded-xl font-semibold text-sm transition-all ${
              loading
                ? 'bg-white/10 text-white/30'
                : 'bg-gradient-to-r from-[#5eead4] to-[#06b6d4] text-[#0D2340] hover:shadow-lg hover:shadow-[#5eead4]/25'
            }`}
            data-testid="interpret-button"
          >
            {loading ? "⏳ Generating interpretation..." : "✨ Interpret All Results"}
          </button>
        </div>
      )}
    </div>
  );
};

// Interpretation Component
const InterpretationSection = ({ interpretation, loading }) => {
  if (!loading && !interpretation) return null;
  
  return (
    <div className="glass-card rounded-xl p-5 border border-[#5eead4]/20" data-testid="interpretation-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full bg-[#5eead4]" />
        <span className="text-[#5eead4] text-[11px] uppercase tracking-widest font-semibold">
          AI Interpretation
        </span>
      </div>
      
      {loading ? (
        <div className="flex gap-1.5 py-2">
          {[0, 1, 2].map(i => (
            <div 
              key={i} 
              className="w-2 h-2 rounded-full bg-[#5eead4]/50 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      ) : (
        <div className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
          {interpretation}
        </div>
      )}
    </div>
  );
};

// History Page Component
const HistoryPage = ({ onBack, onLoadHistory }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchHistory();
  }, []);
  
  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/lab/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history', err);
    } finally {
      setLoading(false);
    }
  };
  
  const deleteHistory = async (id) => {
    try {
      await axios.delete(`${API}/lab/history/${id}`);
      setHistory(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      console.error('Failed to delete history', err);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#5eead4]/50 transition-all"
          data-testid="back-from-history"
        >
          <ArrowLeft className="w-5 h-5 text-white/70" />
        </button>
        <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
          History
        </h2>
      </div>
      
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No saved results yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div key={item.id} className="glass-card rounded-xl p-4" data-testid={`history-item-${item.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    item.mode === 'human' 
                      ? 'bg-blue-500/15 text-blue-400' 
                      : 'bg-amber-500/15 text-amber-400'
                  }`}>
                    {item.mode === 'human' ? '👤 Human' : '🐾 Animal'}
                  </span>
                  {item.panel_type && (
                    <span className="text-white/40 text-xs">{item.panel_type}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/30 text-xs">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => deleteHistory(item.id)}
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                    data-testid={`delete-history-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-white/40 hover:text-red-400" />
                  </button>
                </div>
              </div>
              
              <p className="text-white/50 text-sm mb-3">
                {item.results.length} test{item.results.length !== 1 ? 's' : ''} recorded
              </p>
              
              <button
                onClick={() => onLoadHistory(item)}
                className="w-full py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#5eead4]/50 hover:text-white transition-all flex items-center justify-center gap-2"
                data-testid={`load-history-${item.id}`}
              >
                View Results
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [userId] = useState(getUserId());
  const [credits, setCredits] = useState(0);
  const [mode, setMode] = useState('human');
  const [tab, setTab] = useState('upload');
  const [results, setResults] = useState([]);
  const [interpretation, setInterpretation] = useState('');
  const [loading, setLoading] = useState(false);
  const [interpretLoading, setInterpretLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [panelType, setPanelType] = useState(null);
  const printRef = useRef(null);
  
  // Check for payment success and fetch credits
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // Poll for payment status
      const pollStatus = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            // Clear URL params
            window.history.replaceState({}, '', window.location.pathname);
            fetchCredits();
            setShowLanding(false);
          }
        } catch (err) {
          console.error('Error checking payment status:', err);
        }
      };
      pollStatus();
    }
    
    fetchCredits();
  }, []);
  
  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits/${userId}`);
      setCredits(response.data.credits || 0);
    } catch (err) {
      console.error('Error fetching credits:', err);
    }
  };
  
  const useCredit = async () => {
    if (credits < 1) {
      alert('No credits remaining. Please purchase more credits.');
      setShowLanding(true);
      return false;
    }
    // Credit will be deducted server-side on successful analysis
    return true;
  };
  
  const handleResults = async (newResults, panel = null) => {
    // Deduct credit on successful analysis
    setCredits(prev => Math.max(0, prev - 1));
    
    setResults(newResults);
    setPanelType(panel);
    setInterpretation('');
    
    // Auto-interpret
    setInterpretLoading(true);
    try {
      const response = await axios.post(`${API}/lab/interpret`, {
        results: newResults,
        mode: mode
      });
      setInterpretation(response.data.interpretation);
      
      // Save to history
      await axios.post(`${API}/lab/history`, {
        mode: mode,
        panel_type: panel,
        results: newResults,
        interpretation: response.data.interpretation
      });
    } catch (err) {
      console.error('Failed to interpret results', err);
      setInterpretation('Unable to generate interpretation. Please try again.');
    } finally {
      setInterpretLoading(false);
    }
  };
  
  const handleExportPDF = () => {
    const element = document.getElementById('results-container');
    if (!element) return;
    
    const opt = {
      margin: 10,
      filename: `hikari-lab-results-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save();
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleLoadHistory = (historyItem) => {
    setMode(historyItem.mode);
    setResults(historyItem.results);
    setInterpretation(historyItem.interpretation || '');
    setPanelType(historyItem.panel_type);
    setShowHistory(false);
  };
  
  const resetResults = () => {
    setResults([]);
    setInterpretation('');
    setPanelType(null);
  };
  
  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetResults();
  };
  
  // Show landing page
  if (showLanding) {
    return (
      <LandingPage 
        onGetStarted={() => setShowLanding(false)} 
        userId={userId}
        credits={credits}
      />
    );
  }
  
  // History Page
  if (showHistory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D2340] via-[#0f3460] to-[#1a1a2e]">
        <header className="sticky top-0 z-50 bg-[#0D2340]/80 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🔬</span>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lab Result Reader
              </h1>
            </div>
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-6">
          <HistoryPage 
            onBack={() => setShowHistory(false)} 
            onLoadHistory={handleLoadHistory}
          />
        </main>
        
        <footer className="text-center py-6 px-4 text-white/25 text-xs">
          ⚕️ For educational purposes only · Not medical advice · Consult a qualified healthcare provider or veterinarian · Reference ranges vary by laboratory
        </footer>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2340] via-[#0f3460] to-[#1a1a2e]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0D2340]/80 backdrop-blur-xl border-b border-white/10 no-print">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🔬</span>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lab Result Reader
              </h1>
            </div>
            
            <button
              onClick={() => setShowHistory(true)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#5eead4]/50 hover:text-white transition-all flex items-center gap-2"
              data-testid="open-history"
            >
              <History className="w-4 h-4" />
              History
            </button>
            
            <button
              onClick={() => setShowLanding(true)}
              className="px-3 py-1.5 rounded-lg bg-[#5eead4]/20 border border-[#5eead4]/30 text-[#5eead4] text-sm hover:bg-[#5eead4]/30 transition-all flex items-center gap-2"
              data-testid="credits-btn"
            >
              <CreditCard className="w-4 h-4" />
              {credits} credit{credits !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Hero Section */}
        {results.length === 0 && (
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Lab Result Reader
            </h2>
            <p className="text-white/40 text-sm">
              Upload a PDF or photo of your report · or enter values manually · Human & Animal
            </p>
          </div>
        )}
        
        {/* Mode Toggle */}
        {results.length === 0 && (
          <div className="glass-card rounded-xl p-1.5 flex gap-1 mb-4">
            {["human", "animal"].map(s => (
              <button
                key={s}
                onClick={() => handleModeChange(s)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === s
                    ? 'bg-[#5eead4]/15 text-[#5eead4] border border-[#5eead4]/30'
                    : 'text-white/40 border border-transparent hover:text-white/60'
                }`}
                data-testid={`mode-${s}`}
              >
                {s === "human" ? "👤 Human" : "🐾 Animal / Pet"}
              </button>
            ))}
          </div>
        )}
        
        {/* Tab Toggle */}
        {results.length === 0 && (
          <div className="glass-card rounded-xl p-1.5 flex gap-1 mb-6">
            {[["upload", "📤 Upload Report"], ["manual", "✏️ Enter Manually"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === key
                    ? 'bg-white/10 text-white border border-white/15'
                    : 'text-white/40 border border-transparent hover:text-white/60'
                }`}
                data-testid={`tab-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        
        {/* Content */}
        {results.length === 0 ? (
          tab === 'upload' ? (
            <UploadTab 
              mode={mode} 
              onResults={handleResults}
              setLoading={setLoading}
              loading={interpretLoading}
            />
          ) : (
            <ManualEntryTab 
              mode={mode} 
              onResults={handleResults}
              setLoading={setLoading}
              loading={interpretLoading}
            />
          )
        ) : (
          <div id="results-container" className="space-y-4">
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 no-print">
              <div>
                <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Your Results
                </h2>
                <p className="text-white/40 text-sm">
                  {results.length} test{results.length !== 1 ? 's' : ''} analyzed
                  {panelType && ` · ${panelType}`}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportPDF}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#5eead4]/50 transition-all flex items-center gap-2"
                  data-testid="export-pdf"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </button>
                <button
                  onClick={handlePrint}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#5eead4]/50 transition-all flex items-center gap-2"
                  data-testid="print-results"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={resetResults}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#5eead4]/50 transition-all flex items-center gap-2"
                  data-testid="new-analysis"
                >
                  <X className="w-4 h-4" />
                  New
                </button>
              </div>
            </div>
            
            {/* Results Table */}
            <div className="glass-card rounded-xl p-5">
              <ResultsTable results={results} printRef={printRef} />
            </div>
            
            {/* Interpretation */}
            <InterpretationSection 
              interpretation={interpretation} 
              loading={interpretLoading}
            />
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="text-center py-6 px-4 text-white/25 text-xs no-print">
        ⚕️ For educational purposes only · Not medical advice · Consult a qualified healthcare provider or veterinarian · Reference ranges vary by laboratory
      </footer>
    </div>
  );
}

export default App;
